<template>
  <div class="wo-inner-container">
    <div class="wo-line" />
    <ValidationObserver
      ref="observer"
      v-slot="{ valid, dirty }"
    >
      <WorkOrderSection
        iconName="info_outline"
        :displayShowHideSection="false"
      >
        <template #child>
          <div class="site-info-container">
            <v-toolbar-title class="header-info-sub">
              {{ $t('sites.siteInfo') }}
            </v-toolbar-title>
            <v-col class="modalColPadding">
              <p class="modalXMargin titleModal">
                {{ $t('forms.labels.site') }}
              </p>
            </v-col>
            <v-row class="modalXMargin">
              <v-col
                cols="12"
                sm="8"
                class="modalColPadding"
              >
                <VTextField
                  v-model="data.name"
                  outlined
                  :label="$t('forms.labels.name')"
                  :rules="{
                    required: true,
                    min: 1,
                    max: 200,
                  }"
                  data-cy="siteForm-name"
                  @input="updateData"
                />
              </v-col>
              <v-col
                class="modalColPadding"
                cols="12"
                sm="4"
              >
                <VTextField
                  v-model="data.deliveryAccountNumber"
                  outlined
                  :label="
                    $t('forms.labels.deliveryAccountNumber')
                  "
                  :rules="{
                    required: true,
                    min: 1,
                    max: 20,
                    regex: /^[a-zA-Z0-9-_& ]+$/,
                  }"
                  data-cy="siteForm-delivryAccountNumber"
                  @input="updateData"
                />
              </v-col>
            </v-row>
            <v-col class="modalColPadding">
              <p class="modalXMargin titleModal">
                {{ $t('forms.labels.address') }}
              </p>
            </v-col>
            <v-row class="modalXMargin">
              <v-col class="modalColPadding">
                <AutocompleteAdresse @change="updateAddress" />
              </v-col>
            </v-row>
            <v-row class="modalXMargin">
              <v-col class="modalColPadding">
                <AddressFormPart
                  :details="addressDetails"
                  :isEditable="isForm"
                  :adressWithAutocomplete="true"
                  :timezoneRequired="true"
                  @change="onChangeAddress"
                />
              </v-col>
            </v-row>
            <v-col class="modalColPadding">
              <p class="modalXMargin titleModal">
                {{ $t('forms.labels.lang') }}
              </p>
            </v-col>
            <v-row class="modalXMargin">
              <v-col
                cols="12"
                sm="8"
                class="modalColPadding"
              >
                <VSelect
                  v-model="data.lang"
                  outlined
                  :label="$t('forms.labels.lang')"
                  itemValue="key"
                  itemText="name"
                  :required="true"
                  :validateAtInitialization="
                    !populateWith.empty
                  "
                  :items="langs"
                  data-cy="language"
                  @input="updateData"
                />
              </v-col>
            </v-row>
          </div>
        </template>
      </WorkOrderSection>
      <!-- Note -->
      <WorkOrderSection
        iconName="mdi-pencil"
        :displayShowHideSection="false"
      >
        <template #child>
          <div class="site-info-container">
            <v-toolbar-title class="header-info-sub">
              {{ $t('forms.labels.notes') }}
            </v-toolbar-title>
            <v-row class="modalXMargin">
              <v-col class="modalColPadding">
                <VTextarea
                  v-model="data.notes"
                  outlined
                  :label="$t('forms.labels.notes')"
                  :placeholder="$t('forms.labels.textField')"
                  :rules="{ max: 350 }"
                />
              </v-col>
            </v-row>
          </div>
        </template>
      </WorkOrderSection>
      <WorkOrderSection
        isLast
        iconName="settings"
        :displayShowHideSection="false"
      >
        <template #child>
          <div class="site-info-container">
            <v-toolbar-title class="header-info-sub">
              {{ $t('administration.configuration') }}
            </v-toolbar-title>
            <v-row class="modalXMargin">
              <v-col class="modalColPadding">
                <VAutocomplete
                  v-if="hasPermission('clients.edit')"
                  v-model="data.defaultStandardFamily"
                  :label="$t('standardFamilies.labels.siteDefault')"
                  :items="availableStandardFamilies"
                  outlined
                  :multiple="false"
                  :clearable="true"
                  :loading="loading"
                  :disabled="disabledStandardFamily"
                  @input="updateData"
                />
              </v-col>
            </v-row>
          </div>
        </template>
      </WorkOrderSection>
      <SlotScopeWatcher
        :value="dirty"
        @change="$emit('dirtyChanged', dirty)"
      />
      <SlotScopeWatcher
        :value="valid"
        @change="$emit('validChanged', valid)"
      />
    </ValidationObserver>
  </div>
</template>

<script>
import { ValidationObserver } from 'vee-validate';
import WorkOrderSection from '@/views/workOrders/overview/WorkOrderSection';
import SlotScopeWatcher from '@/components/shared/widgets/forms/validationHelper/SlotScopeWatcher';
import VTextField from '@/components/shared/widgets/forms/VuetifyInputs/VTextField.vue';
import VTextarea from '@/components/shared/widgets/forms/VuetifyInputs/VTextarea.vue';
import AddressFormPart from '@/components/shared/forms-controls/AddressFormPart.vue';
import VAutocomplete from '@/components/shared/widgets/forms/VuetifyInputs/VAutocomplete.vue';
import { LOCALES } from '@/locales/i18n';
import { mapGetters } from 'vuex';
import AutocompleteAdresse from '@/components/AutocompleteAdresse.vue';
import { isEmpty, isEqual } from 'lodash';
import roles from '@/constants/roles';

export default {
    components: {
        AddressFormPart,
        SlotScopeWatcher,
        VAutocomplete,
        VTextField,
        VTextarea,
        ValidationObserver,
        AutocompleteAdresse,
        WorkOrderSection,
    },
    props: {
        populateWith: {
            type: Object,
            default: () => ({ empty: true }),
        },
        isForm: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            data: {
                name: null,
                deliveryAccountNumber: null,
                address: null,
                city: null,
                province: null,
                postalCode: null,
                tz: null,
                country: null,
                lang: null,
                initialSiteStatus: null,
                isFormValid: false,
                notes: '',
                active: true,
                defaultStandardFamily: null,
            },
            availableStandardFamilies: [],
            loading: false,
        };
    },
    computed: {
        ...mapGetters(['hasPermission', 'hasRole']),
        addressDetails() {
            return {
                address: this.data.address,
                city: this.data.city,
                country: this.data.country,
                postalCode: this.data.postalCode,
                tz: this.data.tz,
                province: this.data.province,
                streetNumber: this.data.streetNumber,
                appartmentNumber: this.data.appartmentNumber,
            };
        },
        langs() {
            return LOCALES.map(lang => ({
                name: this.$t(`langs.${lang}`),
                key: lang,
            }));
        },
        disabledStandardFamily() {
            return !(this.hasRole(roles.SALES_REPRESENTATIVE.value)
          || this.hasRole(roles.SALES_MANAGER.value)
          || this.hasRole(roles.MANAGER.value)
          || this.hasRole(roles.ADMINISTRATOR.value));
        },
    },
    watch: {
        populateWith: {
            handler() {
                if (!this.populateWith.empty) {
                    this.data = this.populateWith;
                }
            },
            immediate: true,
        },
        data: {
            handler(newValue, oldValue) {
                const eq = isEqual(newValue, oldValue);
                if (!eq) {
                    this.updateData();
                }
            },
            deep: true,
        },
    },
    async created() {
        // Current site status
        this.data.initialSiteStatus = this.populateWith.active;
        await this.getAvailableStandardFamilies();
    },
    methods: {
        async getAvailableStandardFamilies() {
            this.loading = true;
            const { data } = await this.$rq.getStandardFamilies({ pagination: false });
            this.availableStandardFamilies = data.standardFamilies?.map(family => ({ name: family.name, id: family.id }));
            this.loading = false;
        },
        async updateData() {
            this.isFormValid = await this.$refs.observer.validate();
            if (this.data.defaultStandardFamily === undefined) this.data.defaultStandardFamily = null;
            this.$emit('sendData', {
                ...this.data,
                isFormValid: this.isFormValid,
            });
        },
        async onChangeAddress(details) {
            this.data = {
                ...this.data,
                ...details,
            };
        },
        async updateAddress(address) {
            if (!isEmpty(address)) {
                this.data = {
                    ...this.data,
                    city:
                        address?.locality
                        || address?.sublocality_level_1
                        || address?.administrative_area_level_3
                        || address.administrative_area_level_2
                        || '',
                    country: address?.country || '',
                    province: address?.administrative_area_level_1 || '',
                    postalCode:
                        address?.postal_code || address?.postal_code_prefix || '',
                    streetNumber: address?.street_number || '',
                    address: address?.route || '',
                    tz: address?.tz || '',
                    locationGPS: address?.locationGPS || '',
                };
            }
        },
    },
};
</script>
